import invoicesService from "@/modules/invoices/services/invoicesService";
import { i18n } from "@/plugins/i18n";
import { CURRENCIES } from '@/constansts/currency';
import taxUtil from '@/modules/payments/utils/taxUtil';
import formattingUtil from '@/utils/formattingUtil';

const buildRefundCreditNotePayload = async ({ invoiceUid, sourcePaymentUid, refundAmount, invoiceOriginalAmount }) => {
  const isFullRefund = parseFloat(refundAmount) === parseFloat(invoiceOriginalAmount);

  if (isFullRefund) {
    const invoice = await invoicesService.fetchInvoice(invoiceUid);
    const invoiceLineItems = invoice.items.concat(
      invoice.sections?.flatMap((section) => section.items) || [],
    );

    return {
      invoice_uid: invoiceUid,
      line_items: invoiceLineItems.map((item) => ({
        name: item.name,
        quantity: item.quantity,
        unit_amount: item.rate,
        taxes: item.taxes,
      })),
      notes: i18n.t("refund_dialog.credit_note_reason.full_refund"),
      source_payment_uid: sourcePaymentUid,
    };
  }

  return {
    invoice_uid: invoiceUid,
    line_items: [{
      name: i18n.t("refund_dialog.credit_note_partial_refund_item_name"),
      quantity: 1,
      unit_amount: refundAmount,
    }],
    notes: i18n.t("refund_dialog.credit_note_reason.partial_refund"),
    source_payment_uid: sourcePaymentUid,
  };
};

const calcCreditEditorBreakdown = (state) => {
  const invoiceUid = state.creditNoteEditor?.invoice_uid;
  const invoice = state.invoices[invoiceUid];
  if (!state.creditNoteEditor?.line_items?.length || !invoice) {
    return { netTotal: 0, taxTotal: 0, grandTotal: 0 };
  }

  return state.creditNoteEditor.line_items.reduce((acc, item) => {
    const itemPrice = item.unit_amount * item.quantity;
    const breakdown = taxUtil.calcPriceBreakdown(invoice.metadata.tax_mode, itemPrice, item.taxes || []);

    return {
      netTotal: formattingUtil.roundNumber(acc.netTotal + breakdown.netTotal),
      taxTotal: formattingUtil.roundNumber(acc.taxTotal + breakdown.taxTotal),
      grandTotal: formattingUtil.roundNumber(acc.grandTotal + breakdown.grandTotal),
    };
  }, { netTotal: 0, taxTotal: 0, grandTotal: 0 });
};

const state = {
  invoices: {},
  creditNoteEditor: null,
};

const getters = {
  invoiceByUid: (state) => (uid) => state.invoices[uid],
  creditNoteEditor: (state) => state.creditNoteEditor,
  creditNoteCurrencySymbol: (state) => {
    const invoiceUid = state.creditNoteEditor?.invoice_uid;
    const invoice = state.invoices[invoiceUid];
    return CURRENCIES[invoice?.currency]?.symbol || '';
  },
  creditAvailable: (state, localGetters) => {
    const invoiceUid = state.creditNoteEditor?.invoice_uid;
    const invoice = state.invoices[invoiceUid];
    if (!invoice || !invoice.payment_status || !invoice.amount) return '';

    const total = parseFloat(invoice.amount) || 0;
    const credited = parseFloat(invoice.payment_status.amount_credited) || 0;
    const available = total - credited;

    return `${localGetters.creditNoteCurrencySymbol}${formattingUtil.roundNumber(available)}`;
  },
  creditEditorSubtotal: (state, localGetters) => {
    const { netTotal } = calcCreditEditorBreakdown(state);
    if (!netTotal) return '';
    return `${localGetters.creditNoteCurrencySymbol}${netTotal}`;
  },
  creditEditorTax: (state, localGetters) => {
    const { taxTotal } = calcCreditEditorBreakdown(state);
    if (!taxTotal) return '';
    return `${localGetters.creditNoteCurrencySymbol}${taxTotal}`;
  },
  creditEditorTotal: (state, localGetters) => {
    const { grandTotal } = calcCreditEditorBreakdown(state);
    if (!grandTotal) return '';
    return `${localGetters.creditNoteCurrencySymbol}${grandTotal}`;
  },
};

const mutations = {
  setInvoice(state, invoice) {
    state.invoices = {
      ...state.invoices,
      [invoice.uid]: invoice,
    };
  },
  updateInvoice(state, updatedFields) {
    state.invoices = {
      ...state.invoices,
      [updatedFields.uid]: {
        ...state.invoices[updatedFields.uid],
        ...updatedFields,
      },
    };
  },
  setCreditNoteEditor(state, creditNote) {
    state.creditNoteEditor = creditNote;
  },
  updateCreditNoteEditor(state, updates) {
    state.creditNoteEditor = {
      ...state.creditNoteEditor,
      ...updates,
    };
  },
  resetCreditNoteEditor(state) {
    state.creditNoteEditor = null;
  },
};

const actions = {
  async fetchInvoice({ commit }, invoiceUid) {
    const invoice = await invoicesService.fetchInvoice(invoiceUid);
    commit("setInvoice", invoice);
    return invoice;
  },
  initializeCreditNoteEditor({ commit, state }, invoiceUid) {
    const invoice = state.invoices[invoiceUid];
    if (!invoice) return;

    const invoiceLineItems = (invoice.items || []).concat(
      invoice.sections?.flatMap((section) => section.items) || [],
    );

    const lineItems = invoiceLineItems.map((item, index) => ({
      id: `${invoiceUid}-${index}`,
      name: item.name,
      quantity: item.quantity,
      unit_amount: item.rate,
      taxes: item.taxes || [],
      isFromInvoice: true,
    }));

    const creditNote = {
      invoice_uid: invoiceUid,
      line_items: lineItems,
      notes: '',
    };

    commit('setCreditNoteEditor', creditNote);
  },
  resetCreditNoteEditor({ commit }) {
    commit('resetCreditNoteEditor');
  },
  updateCreditNoteEditor({ commit }, updates) {
    commit('updateCreditNoteEditor', updates);
  },
  async cancelInvoice({ commit }, { payableUid, payload }) {
    const cancelInvoiceResponse = await invoicesService.cancelInvoice(payableUid, payload);
    commit("updateInvoice", cancelInvoiceResponse.data.invoice);
    return cancelInvoiceResponse;
  },
  async createCreditNote(
    { dispatch },
    { creditNotePayload, invoiceUid, sourcePaymentUid, refundAmount, invoiceOriginalAmount }
  ) {
    const payload = creditNotePayload
      || await buildRefundCreditNotePayload({ invoiceUid, sourcePaymentUid, refundAmount, invoiceOriginalAmount });

    const response = await invoicesService.createCreditNote(payload);

    if (sourcePaymentUid) {
      dispatch("PaymentsStore/fetchPayment", { uid: sourcePaymentUid }, { root: true });
    }

    return response;
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
